import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  GitCompare,
  RefreshCw,
  X,
} from 'lucide-react';
import MainService from '../../services/MainService';

const COMPARISON_FIELDS = [
  { key: 'Remarks', label: 'History Remarks', meta: true },
  { key: 'UserName', label: 'Changed By', meta: true },
  { key: 'StatusName', label: 'Status' },
  { key: 'StatusKeyword', label: 'Status Keyword' },
  { key: 'VoucherNo', label: 'Voucher No' },
  { key: 'ClientBillNo', label: 'Client Bill No' },
  { key: 'ClientPayNo', label: 'Client Pay No' },
  { key: 'ClientRetNo', label: 'Client Return No' },
  { key: 'ShippingNo', label: 'Shipping No' },
  { key: 'DeliveryNo', label: 'Delivery No' },
  { key: 'PickUpNo', label: 'Pickup No' },
  { key: 'BatchNo', label: 'Batch No' },
  { key: 'PaymentRefNo', label: 'Payment Ref' },
  { key: 'ReturnRefNo', label: 'Return Ref' },
  { key: 'StoreRefNo', label: 'Store Ref' },
  { key: 'ChargeFrom', label: 'Charge From' },
  { key: 'PayType', label: 'Pay Type' },
  { key: 'PaymentStatus', label: 'Payment Status' },
  { key: 'ReturnStatus', label: 'Return Status' },
  { key: 'Attempts', label: 'Attempts' },
  { key: 'Quantity', label: 'Quantity' },
  { key: 'ProductPrice', label: 'Product Price' },
  { key: 'ACTProductPrice', label: 'ACT Product Price' },
  { key: 'MainProductPrice', label: 'Main Product Price' },
  { key: 'DeliveryCharge', label: 'Delivery Charge' },
  { key: 'TAX', label: 'TAX' },
  { key: 'CustomerAmount', label: 'Customer Amount' },
  { key: 'ClientAmount', label: 'Client Amount' },
  { key: 'CommissionAmount', label: 'Commission Amount' },
  { key: 'OrderDate', label: 'Order Date', type: 'date' },
  { key: 'PickupDate', label: 'Pickup Date', type: 'date' },
  { key: 'DeliveryDate', label: 'Delivery Date', type: 'date' },
  { key: 'TypeName', label: 'Type' },
  { key: 'ReasonName', label: 'Reason' },
  { key: 'AgentName', label: 'Agent' },
  { key: 'PickUpByName', label: 'Pickup By' },
  { key: 'DeliverByName', label: 'Deliver By' },
  { key: 'IsSMSSend', label: 'SMS Sent', type: 'bool' },
  { key: 'IsExchange', label: 'Exchange', type: 'bool' },
  { key: 'IsPickup', label: 'Pickup', type: 'bool' },
  { key: 'IsFragile', label: 'Fragile', type: 'bool' },
  { key: 'IsFree', label: 'Free', type: 'bool' },
  { key: 'IsAdvancePay', label: 'Advance Pay', type: 'bool' },
  { key: 'IsAdvancePrice', label: 'Advance Price', type: 'bool' },
  { key: 'IsActive', label: 'Active', type: 'bool' },
  { key: 'OrderRemarks', label: 'Order Remarks' },
  { key: 'DriverRemarks', label: 'Driver Remarks' },
  { key: 'ClientNo', label: 'Client No' },
  { key: 'ClientName', label: 'Client Name' },
  { key: 'ClientMobile', label: 'Client Mobile' },
  { key: 'ClientAddress', label: 'Client Address' },
  { key: 'ClientLocationName', label: 'Client Location' },
  { key: 'ClientEmirateName', label: 'Client Emirate' },
  { key: 'ClientPOBOX', label: 'Client PO Box' },
  { key: 'CustomerName', label: 'Customer Name' },
  { key: 'CustomerMobile', label: 'Customer Mobile' },
  { key: 'CustomerMobile2', label: 'Customer Mobile 2' },
  { key: 'CustomerAddress', label: 'Customer Address' },
  { key: 'CustomerLocationName', label: 'Customer Location' },
  { key: 'CustomerEmirateName', label: 'Customer Emirate' },
  { key: 'CustomerPOBOX', label: 'Customer PO Box' },
  { key: 'DeliveryLAT', label: 'Delivery LAT' },
  { key: 'DeliveryLNG', label: 'Delivery LNG' },
  { key: 'PickupLAT', label: 'Pickup LAT' },
  { key: 'PickupLNG', label: 'Pickup LNG' },
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const normalizeValue = (raw, type) => {
  if (raw == null || raw === '') return '—';
  if (type === 'bool') return raw ? 'Yes' : 'No';
  if (type === 'date') return formatDate(raw);
  if (typeof raw === 'number') {
    if (Number.isInteger(raw)) return String(raw);
    return Number(raw).toFixed(2);
  }
  return String(raw);
};

const getFieldRaw = (history, field) => {
  if (field.key === 'UserName') return history?.User?.UserName ?? history?.UserName ?? '';
  return history?.[field.key];
};

const valuesEqual = (a, b) => String(a ?? '') === String(b ?? '');

const OrderHistoryComparison = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [voucherNo, setVoucherNo] = useState('');
  const [histories, setHistories] = useState([]);
  const [changesOnly, setChangesOnly] = useState(true);
  const [leftIndex, setLeftIndex] = useState(0);
  const [rightIndex, setRightIndex] = useState(1);
  const [mode, setMode] = useState('timeline'); // timeline | pair

  const loadComparison = useCallback(async () => {
    if (!orderId) {
      setError('Order id is required');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const response = await MainService.OrderHistoryComparisonByOrderId(orderId);
      const list = Array.isArray(response?.Histories)
        ? response.Histories
        : Array.isArray(response)
          ? response
          : [];
      // Ensure oldest → newest even if API returns descending
      const sorted = [...list].sort((a, b) => {
        const da = new Date(a.Date || 0).getTime();
        const db = new Date(b.Date || 0).getTime();
        if (da !== db) return da - db;
        return (a.Id || 0) - (b.Id || 0);
      });
      setHistories(sorted);
      setVoucherNo(response?.VoucherNo || sorted[0]?.VoucherNo || sorted[0]?.Order?.VoucherNo || '');
      setLeftIndex(0);
      setRightIndex(Math.max(sorted.length - 1, 0));
    } catch (err) {
      console.error('Order history comparison error:', err);
      setError('Failed to load history comparison');
      setHistories([]);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadComparison();
  }, [loadComparison]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  const timelineColumns = histories;

  const pairColumns = useMemo(() => {
    if (histories.length === 0) return [];
    const left = histories[Math.min(leftIndex, histories.length - 1)];
    const right = histories[Math.min(rightIndex, histories.length - 1)];
    if (!left) return [];
    if (!right || left.Id === right.Id) return [left];
    return [left, right];
  }, [histories, leftIndex, rightIndex]);

  const activeColumns = mode === 'pair' ? pairColumns : timelineColumns;

  const fieldRows = useMemo(() => {
    if (activeColumns.length === 0) return [];

    return COMPARISON_FIELDS.map((field) => {
      const cells = activeColumns.map((history, colIndex) => {
        const display = normalizeValue(getFieldRaw(history, field), field.type);
        const prevDisplay =
          colIndex === 0
            ? display
            : normalizeValue(getFieldRaw(activeColumns[colIndex - 1], field), field.type);
        const changed = colIndex > 0 && !valuesEqual(display, prevDisplay);
        return { display, changed };
      });
      const hasChange = cells.some((c) => c.changed);
      return { field, cells, hasChange };
    }).filter((row) => (changesOnly ? row.hasChange : true));
  }, [activeColumns, changesOnly]);

  const changedFieldCount = useMemo(() => {
    if (activeColumns.length < 2) return 0;
    return COMPARISON_FIELDS.filter((field) => {
      for (let i = 1; i < activeColumns.length; i += 1) {
        const cur = normalizeValue(getFieldRaw(activeColumns[i], field), field.type);
        const prev = normalizeValue(getFieldRaw(activeColumns[i - 1], field), field.type);
        if (!valuesEqual(cur, prev)) return true;
      }
      return false;
    }).length;
  }, [activeColumns]);

  return (
    <div className="space-y-4 pb-8">
      {error && (
        <div className="fixed top-4 right-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 shadow-lg">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1 text-sm">{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-[#46955A]/10 p-2.5 text-[#46955A]">
              <GitCompare className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">History Comparison</h1>
              <p className="mt-1 text-sm text-gray-500">
                Field-by-field comparison of order history snapshots.
                {voucherNo ? (
                  <>
                    {' '}Voucher <span className="font-semibold text-gray-800">{voucherNo}</span>
                  </>
                ) : null}
                {orderId ? (
                  <>
                    {' '}· Order #{orderId}
                  </>
                ) : null}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              type="button"
              onClick={loadComparison}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-[#46955A] px-3 py-2 text-sm font-medium text-[#46955A] hover:bg-[#f0f9f4] disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
              type="button"
              onClick={() => setMode('timeline')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                mode === 'timeline' ? 'bg-white text-[#46955A] shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Full timeline
            </button>
            <button
              type="button"
              onClick={() => setMode('pair')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                mode === 'pair' ? 'bg-white text-[#46955A] shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Compare two
            </button>
          </div>

          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={changesOnly}
              onChange={(e) => setChangesOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[#46955A] focus:ring-[#46955A]"
            />
            Show changed fields only
          </label>

          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm bg-amber-200 ring-1 ring-amber-400" />
              Changed from previous
            </span>
            <span>
              {histories.length} snapshot{histories.length === 1 ? '' : 's'}
              {activeColumns.length >= 2 ? ` · ${changedFieldCount} field(s) differ` : ''}
            </span>
          </div>
        </div>

        {mode === 'pair' && histories.length >= 2 && (
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">From (older / left)</label>
              <select
                value={leftIndex}
                onChange={(e) => setLeftIndex(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#46955A] focus:outline-none"
              >
                {histories.map((h, idx) => (
                  <option key={h.Id ?? idx} value={idx}>
                    #{idx + 1} · {formatDateTime(h.Date)} · {h.Remarks || 'No remarks'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">To (newer / right)</label>
              <select
                value={rightIndex}
                onChange={(e) => setRightIndex(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#46955A] focus:outline-none"
              >
                {histories.map((h, idx) => (
                  <option key={h.Id ?? idx} value={idx}>
                    #{idx + 1} · {formatDateTime(h.Date)} · {h.Remarks || 'No remarks'}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-gray-500">
            <RefreshCw className="h-8 w-8 animate-spin text-[#46955A]" />
            <span className="text-sm">Loading history comparison…</span>
          </div>
        ) : histories.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-gray-500">
            No order history snapshots found for this order.
          </div>
        ) : fieldRows.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-gray-500">
            {changesOnly
              ? 'No field differences between the selected snapshots. Turn off “Show changed fields only” to see all values.'
              : 'No fields to display.'}
          </div>
        ) : (
          <div className="master-table-scroll max-h-[75vh] overflow-auto">
            <table className="min-w-full border-separate border-spacing-0 text-left text-xs">
              <thead className="sticky top-0 z-20">
                <tr>
                  <th className="sticky left-0 z-30 min-w-[160px] border-b border-r border-emerald-300 bg-emerald-100 px-3 py-2.5 font-semibold text-emerald-900">
                    Field
                  </th>
                  {activeColumns.map((history, idx) => (
                    <th
                      key={history.Id ?? idx}
                      className="min-w-[180px] max-w-[240px] border-b border-emerald-300 bg-emerald-100 px-3 py-2.5 font-semibold text-emerald-900"
                    >
                      <div className="text-[11px] uppercase tracking-wide text-emerald-800/80">
                        Snapshot {mode === 'pair' ? (idx === 0 ? 'A' : 'B') : idx + 1}
                      </div>
                      <div className="mt-0.5 text-sm font-bold text-emerald-950">
                        {formatDateTime(history.Date)}
                      </div>
                      <div className="mt-1 line-clamp-2 text-[11px] font-normal text-emerald-900/80" title={history.Remarks || ''}>
                        {history.Remarks || '—'}
                      </div>
                      <div className="mt-0.5 text-[11px] font-normal text-emerald-800/70">
                        by {history.User?.UserName || history.UserName || '—'}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fieldRows.map(({ field, cells }) => (
                  <tr key={field.key} className="group">
                    <td className="sticky left-0 z-10 whitespace-nowrap border-b border-r border-gray-200 bg-gray-50 px-3 py-2 font-medium text-gray-700 group-hover:bg-gray-100">
                      {field.label}
                    </td>
                    {cells.map((cell, idx) => (
                      <td
                        key={`${field.key}-${idx}`}
                        className={`border-b border-gray-100 px-3 py-2 align-top ${
                          cell.changed
                            ? 'bg-amber-100 text-amber-950 ring-1 ring-inset ring-amber-300'
                            : 'bg-white text-gray-800'
                        }`}
                        title={cell.changed ? 'Changed from previous snapshot' : undefined}
                      >
                        <span className={cell.changed ? 'font-semibold' : ''}>{cell.display}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistoryComparison;
